import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import AgTable from '@navi/web-ui/lib/components/AgTable/AgTable';
import { DropDownPosition } from '@navi/web-ui/lib/components/Pagination/constant';
import Pagination from '@navi/web-ui/lib/components/Pagination/Pagination';
import { AgGridReact } from 'ag-grid-react';
import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { createQueryParams, readQueryParams } from 'src/utils/QueryParamsHelper';
import { IRenderAgTable } from '../types';
import { handleGridReady } from './ColumnDefs';
import styles from './commonStyles.module.scss';
import { ColDefsType } from '@navi/web-ui/lib/components/AgTable/types';
import { noop } from '@navi/web-ui/lib/utils/common';
import cx from 'classnames';

const RenderAgTable: React.FC<IRenderAgTable> = props => {
  const {
    colDefs,
    data,
    pageDetails,
    tableName,
    tableHeight = 400,
    callBackFn,
    isTableHorizontallyScrollable = false,
    isServerSortable = false,
    sizeColumnsToFit = true,
    headerHeight = 80,
    customTableClass,
    ...rest
  } = props;
  const params = readQueryParams();
  const navigate = useNavigate();
  const location = useLocation();
  const gridRef = useRef<AgGridReact>(null);

  const { tabId = '' } = useParams();

  const handleSortChanged = (column: ColDefsType) => {
    if (!column?.serverSortable) return;
    const params = readQueryParams();
    if (!params) return;
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_FIELD_DASHBOARD_SORT_CHANGED, {
      sortColumn: column?.field,
      sortOrder: column?.serverSort?.toUpperCase(),
      section: tabId,
      tableName: tableName
    });
    const updatedParams = { ...params };
    updatedParams[tableName].sortByField = column.serverSort ? column?.field : '';
    updatedParams[tableName].sortOrder = column?.serverSort
      ? column?.serverSort?.toUpperCase()
      : '';
    const paramsData = createQueryParams(updatedParams);
    navigate(paramsData);
    callBackFn?.();
  };

  const handlePageChange = (pageNo: number) => {
    if (!params) return;
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_FIELD_DASHBOARD_PAGE_CHANGED, {
      newPageNo: pageNo,
      section: tabId,
      oldPageNo: pageDetails?.currentPage,
      tableName: tableName
    });
    const updatedParams = { ...params };
    updatedParams[tableName].pageNumber = String(pageNo);
    const paramsData = createQueryParams(updatedParams);
    navigate(paramsData);
    callBackFn?.();
  };

  const handleSizeChange = (pageSize: number) => {
    if (!params) return;
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_FIELD_DASHBOARD_PAGE_SIZE_CHANGED, {
      newPageSize: pageSize,
      section: tabId,
      oldPageSize: pageDetails?.pageSize,
      tableName: tableName
    });
    const updatedParams = { ...params };
    updatedParams[tableName].pageNumber = '1'; // on change setting it to default page number 1
    updatedParams[tableName].pageSize = String(pageSize);
    const paramsData = createQueryParams(updatedParams);
    navigate(paramsData);
    callBackFn?.();
  };

  useEffect(() => {
    if (sizeColumnsToFit && gridRef?.current?.api?.sizeColumnsToFit) {
      gridRef?.current?.api?.sizeColumnsToFit();
    }
  }, [tabId, data]);

  return (
    <div className="isolate pl-6 pr-6">
      <AgTable
        ref={gridRef}
        key={tableName}
        className={cx(
          'senseiAgTable',
          { senseiAgTableScroll: !isTableHorizontallyScrollable },
          customTableClass
        )}
        columnDefs={colDefs}
        rowData={data}
        onGridReady={e => handleGridReady(e, gridRef, sizeColumnsToFit)}
        defaultColDef={{
          suppressMovable: true,
          wrapText: true,
          autoHeight: true
        }}
        style={{
          height: tableHeight
        }}
        headerHeight={headerHeight}
        theme={'alpine'}
        sizeColumnsToFit={sizeColumnsToFit}
        alternateRowColor="#F8FBFF"
        onServerSortChanged={isServerSortable ? handleSortChanged : noop}
        paginationWrapperClasses={styles.paginationWrapperClasses}
        PaginationComponent={
          pageDetails ? (
            <Pagination
              key={location.search}
              pageSize={pageDetails?.pageSize}
              currentPage={pageDetails?.currentPage}
              totalCount={pageDetails?.totalElements}
              pageSizeOptions={pageDetails?.pageSizeOptions}
              onPageChange={handlePageChange}
              onPageSizeChange={handleSizeChange}
              pageNumberDropDownPosition={DropDownPosition.TOP}
            />
          ) : null
        }
        {...rest}
      />
    </div>
  );
};

export default RenderAgTable;
